<script setup lang="ts">
import TransitionHeight from '../transition/TransitionHeight.vue'
import type { InputType } from './types'

type Props = {
  label?: string
  clearable?: boolean
  disabled?: boolean
  placeholder?: string
  errorMessages?: string | string[] | null
  loading?: boolean
  type?: InputType
  width?: string | number
  hideDetails?: boolean
  dynamicDetails?: boolean
  autocomplete?: string
  autofocus?: boolean
  maxlength?: string | number
  minlength?: string | number
  prefix?: string
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  clearable: true,
  placeholder: '',
  label: '',
  errorMessages: null,
  type: 'text',
  width: '',
  autocomplete: 'on',
  maxlength: undefined,
  minlength: undefined,
  prefix: undefined,
  class: '',
})

const emit = defineEmits(['update:model-value', 'clear'])

const model = defineModel<string>()

const clear = () => {
  model.value = ''
  emit('clear')
}

const inputEl = ref<HTMLElement | null>(null)

onMounted(() => {
  if (props.autofocus && inputEl.value) {
    inputEl.value.focus()
  }
})

const onClickControl = () => {
  if (!inputEl.value) {
    return
  }

  inputEl.value.focus()
}

const isFieldFocused = ref(false)

const onFocusInput = () => {
  isFieldFocused.value = true
}

const onBlurInput = () => {
  isFieldFocused.value = false
}

const isActive = computed(() => {
  return isFieldFocused.value || model.value
})

const errorMessage = computed(() => {
  if (!props.errorMessages) {
    return null
  }

  if (Array.isArray(props.errorMessages)) {
    return props.errorMessages[0]
  }

  return props.errorMessages
})

const inputStyles = computed(() => {
  const styles = []

  if (props.width) {
    switch (typeof props.width) {
      case 'string':
        if (props.width.includes('px') || props.width.includes('%')) {
          styles.push(`width: ${props.width}`)
        } else {
          styles.push(`width: ${props.width}px`)
        }
        break

      case 'number':
        styles.push(`width: ${props.width}px`)
        break
    }
  }

  return styles
})

const inputClasses = computed(() => [
  'app-input',
  props.class,
  {
    'app-input--active': isActive.value,
    'app-input--error': errorMessage.value,
    'app-input--loading': props.loading,
  },
])

const inputType = ref(props.type)

const onChangeInputType = (type: InputType) => {
  inputType.value = type
}

const detailsClasses = computed(() => [
  'app-input__details',
  {
    // Если нужно отображать подсказку динамически, при наличии ошибок
    'app-input__details--dynamic-height': props.dynamicDetails,
  },
])
</script>

<template>
  <div :class="inputClasses" :style="inputStyles">
    <div v-if="label" class="app-input__label">{{ label }}</div>

    <div class="app-input__input">
      <div class="app-input__control" @click="onClickControl">
        <div v-if="$slots['prepend-inner']" class="app-input__prepend">
          <slot name="prepend-inner" />
        </div>

        <div class="app-input__text">
          <div
            v-if="prefix"
            class="app-input__prefix"
            :class="{
              'app-input__prefix--active': model,
            }"
          >
            {{ prefix }}
          </div>

          <input
            ref="inputEl"
            v-model="model"
            v-bind="$attrs"
            class="app-input__field"
            :type="inputType"
            :disabled="disabled"
            :placeholder="placeholder"
            :autocomplete="autocomplete"
            :maxlength="maxlength"
            :minlength="minlength"
            @focus="onFocusInput"
            @blur="onBlurInput"
          />
        </div>

        <div v-if="loading" class="app-input__loader" role="progressbar">
          <the-loader variant="linear" />
        </div>

        <div v-if="$slots['append-inner']" class="app-input__append">
          <slot name="append-inner" />
        </div>

        <div v-else-if="type === 'password'" class="app-input__password-icon">
          <transition name="ui-fade" mode="out-in">
            <the-icon
              v-if="inputType === 'password'"
              class="app-input__action-icon"
              name="view"
              color="var(--ui-dark-grey)"
              clickable
              @click="onChangeInputType('text')"
            />

            <the-icon
              v-else
              class="app-input__action-icon"
              name="view-off"
              color="var(--ui-dark-grey)"
              clickable
              @click="onChangeInputType('password')"
            />
          </transition>
        </div>

        <div v-else-if="clearable && !disabled" class="app-input__clear-icon">
          <transition name="ui-fade">
            <the-icon
              v-show="modelValue"
              class="app-input__action-icon"
              name="close"
              color="var(--ui-unactive-gray)"
              clickable
              @click="clear"
            />
          </transition>
        </div>
      </div>

      <div v-if="!props.hideDetails" :class="detailsClasses">
        <TransitionHeight>
          <div v-if="errorMessage" class="app-input__message">
            {{ errorMessage }}
          </div>
        </TransitionHeight>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '../../assets/ui-layer/styles/mixins';

.app-input {
  width: 100%;
  color: var(--ui-text-primary);

  &__label {
    margin-bottom: 2px;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
  }

  /* Default */
  &__control {
    position: relative;
    display: flex;
    gap: 8px;
    align-items: center;
    padding-right: 8px;
    padding-left: 12px;
    overflow: hidden;
    font-weight: 600;
    cursor: text;
    background-color: var(--ui-grey);
    border: 1px solid var(--ui-grey);
    border-radius: 10px;
    box-shadow: unset;
    transition: border-color 0.3s ease, color 0.3s ease,
      background-color 0.3s ease;

    &:hover {
      background-color: var(--ui-grey--hover);
    }

    @include media('>=desktop-mini') {
      padding-left: 8px;
    }
  }

  &__text {
    display: flex;
    gap: 4px;
    align-items: center;
    width: 100%;
  }

  &__prefix {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    line-height: 38px;
    color: var(--ui-text-secondary);
    cursor: default;
    transition: color 0.3s ease;

    &--active {
      color: var(--ui-text-primary);
    }
  }

  &__field {
    all: unset;
    width: 100%;
    height: 38px;
    min-height: unset;
    padding: 0;
    font-size: 14px;
    line-height: 40px;
    color: var(--ui-text-primary);

    &::placeholder {
      font-size: 14px;
      line-height: 40px;
      color: var(--ui-text-secondary);
    }
  }

  /* Loading */
  &__loader {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 2px;
  }

  /* Icons */
  &__prepend,
  &__append {
    display: flex;
    align-items: center;
    justify-content: center;

    :deep(svg) {
      fill: var(--ui-dark-grey);
      stroke: var(--ui-dark-grey);
    }
  }

  &--active {
    .app-input__prepend,
    .app-input__append,
    .app-input__password-icon {
      :deep(svg) {
        fill: var(--ui-secondary);
        stroke: var(--ui-secondary);
      }
    }
  }

  &__clear-icon,
  &__password-icon {
    width: 24px;
    height: 24px;
    user-select: none;
  }

  &__action-icon {
    &:hover {
      --fillColor: var(--ui-secondary) !important;
      --strokeColor: var(--ui-secondary) !important;
    }
  }

  /* Error */
  &__details {
    display: flex;
    min-height: 16px;
    padding: 0;
    margin-top: 2px;
    overflow: hidden;

    &--dynamic-height {
      min-height: 0;
      margin: 0;

      .app-input__message {
        padding-top: 2px;
      }
    }
  }

  .app-input__message {
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: var(--ui-error);
    word-break: break-word;
  }

  &--error {
    .app-input__control {
      border-color: var(--ui-error);
    }
  }

  /* Disabled */
  &__input:has(input:disabled) {
    pointer-events: none;
    user-select: none;
    opacity: 0.5;

    .app-input__control {
      cursor: unset;

      &:hover {
        background-color: var(--ui-grey);
      }
    }
  }
}
</style>
